import { CURRENT_ENV_ROLE_TYPE } from '../../utils/EnvConfigUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';

/**
 * Role theming for every AI interface (chat, summarization, suggested WOs, …),
 * per the design guideline: buyer surfaces are purple, supplier surfaces cyan,
 * with a 2px stroke.
 *
 * Pass the **session's** `userType` (via `useOscarTheme`, which reads
 * `OscarRoleProvider`). `CURRENT_ENV_ROLE_TYPE` is only the last-resort
 * fallback: it defaults to SUPPLIER whenever `REACT_APP_CURRENT_ENV_ROLE_TYPE`
 * is unset, so a plain `yarn start` claims supplier no matter who is signed in.
 *
 * It also cannot live in `tailwind.config.js`: the `prebuild:*` scripts that
 * compile Tailwind do not export `REACT_APP_CURRENT_ENV_ROLE_TYPE` (it is
 * exported inside `build:*`, which runs after), so a role-dependent config would
 * silently ship buyer colours in the supplier build.
 *
 * Class names are written out in full — Tailwind's scanner only sees literals.
 */
export interface OscarThemeClasses {
	/** Panel / card background fill. */
	surface: string;
	/** 2px stroke in the role's accent, per the guideline. */
	stroke: string;
	/** 1px dashed accent stroke — the inline Ask Oscar input on record pages and the AI containers on the work order Overview tab. */
	dashedStroke: string;
	/** Light accent border for chips on an AI surface. `!` because antd's button CSS loads last. */
	chipBorder: string;
	/** A pressed antd icon button on an AI surface (a chosen rating). `!` for the same reason. */
	selectedAction: string;
	/** Accent text and the mark's linework. */
	accentText: string;
	/** Hover fill for interactive surfaces sitting on `surface`. */
	surfaceHover: string;
	/** Soft fill behind the mark in the empty state. */
	accentSoft: string;
	/** The panel's leading edge — the strongest accent on the surface. */
	panelEdge: string;
	/** Border around the panel's white body card. */
	panelCardBorder: string;
	/** A primary button's fill and border on an AI surface (approve, next). `!` beats antd's own. */
	accentFill: string;
	/** Icon-button hover tint. */
	accentHoverText: string;
}

const BUYER_THEME: OscarThemeClasses = {
	surface: 'bg-purple-0',
	stroke: 'border-2 border-solid border-purple-3',
	dashedStroke: 'border border-dashed border-purple-3',
	chipBorder: '!border-purple-2',
	selectedAction: '!bg-purple-0 !text-[#54298e]',
	accentText: 'text-[#54298e]',
	surfaceHover: 'hover:bg-purple-3/20',
	accentSoft: 'bg-purple-2',
	panelEdge: 'border-[#54298e]',
	panelCardBorder: 'border-purple-2',
	// Figma calls this Purple/7 #54298e, which disagrees with the config's
	// purple-7 (#531dab) — see DESIGN_TOKEN_GAPS.md.
	accentFill: '!border-[#54298e] !bg-[#54298e]',
	accentHoverText: 'hover:text-[#54298e]',
};

/**
 * Cyan/1 (#F0F7F7) and Cyan/3 (#A7D5D9) per design. `surfaceHover` and
 * `accentSoft` are not in any Figma node — they are derived from the accent so
 * both roles behave the same way.
 */
const SUPPLIER_THEME: OscarThemeClasses = {
	surface: 'bg-cyan-1',
	stroke: 'border-2 border-solid border-cyan-3',
	dashedStroke: 'border border-dashed border-cyan-3',
	chipBorder: '!border-cyan-3',
	selectedAction: '!bg-cyan-1 !text-cyan-7',
	accentText: 'text-cyan-7',
	surfaceHover: 'hover:bg-cyan-3/20',
	accentSoft: 'bg-cyan-3/50',
	panelEdge: 'border-cyan-7',
	panelCardBorder: 'border-cyan-3',
	accentFill: '!border-cyan-7 !bg-cyan-7',
	accentHoverText: 'hover:text-cyan-7',
};

export const getOscarTheme = (roleType?: string): OscarThemeClasses =>
	(roleType || CURRENT_ENV_ROLE_TYPE) === ROLE_TYPES.SUPPLIER ? SUPPLIER_THEME : BUYER_THEME;

export { BUYER_THEME, SUPPLIER_THEME };
